/**
 * The diff block a file edit shows in place of its arguments.
 *
 * Unified, not side by side. The thread lives in a narrow column beside the
 * canvas, and two columns of code inside that one leaves ~30 characters a side,
 * which is not enough to read a line of anything.
 *
 * The gutter is a sign, not a line number (see `editDiff` for why there are no
 * honest numbers to show). Colour carries the same information a second time,
 * deliberately: the sign survives a colour-blind reader, the tint survives a
 * skim.
 */

import type { DiffRow, EditPreview } from './editDiff'

const ROW_CLASS: Record<DiffRow['type'], string> = {
  added: 'bg-green-500/10 text-green-700 dark:text-green-300',
  removed: 'bg-red-500/10 text-red-700 dark:text-red-300',
  context: 'text-muted-foreground',
  gap: '',
}

const SIGN: Record<DiffRow['type'], string> = {
  added: '+',
  removed: '-',
  context: ' ',
  gap: '',
}

/** `+12 −3`, the stat the collapsed row carries. Empty when nothing changed. */
export function EditStat({ preview }: { preview: EditPreview }) {
  return (
    <span className="shrink-0 font-mono text-[0.65rem]">
      {preview.added > 0 ? (
        <span className="text-green-600 dark:text-green-400">
          +{preview.added}
        </span>
      ) : null}
      {preview.added > 0 && preview.removed > 0 ? ' ' : null}
      {preview.removed > 0 ? (
        <span className="text-red-600 dark:text-red-400">
          −{preview.removed}
        </span>
      ) : null}
    </span>
  )
}

export function EditDiffView({ preview }: { preview: EditPreview }) {
  return (
    // NO HEADER. The row this opens from is directly above it and already says
    // the file and the `+N −N`; repeating both here just draws a second title
    // bar one line under the first.
    <div className="mb-2 overflow-hidden rounded border border-border">
      {/* One scroller for the whole block, so every row shares a horizontal
          origin: per-row scrolling would let the tints slide out of alignment
          and stop reading as columns. */}
      <div className="overflow-x-auto bg-black/20">
        {preview.rows.map((row, i) => {
          if (row.type === 'gap') {
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed render of one diff; rows are never reordered
                key={i}
                className="border-border/60 border-y bg-muted/20 px-2 py-0.5 font-mono text-[0.65rem] text-muted-foreground/70"
              >
                {row.text ? `⋯ ${row.text}` : '⋯'}
              </div>
            )
          }
          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: a fixed render of one diff; rows are never reordered
              key={i}
              className={`flex font-mono text-[0.7rem] leading-[1.5] ${ROW_CLASS[row.type]}`}
            >
              <span
                aria-hidden
                className="w-4 shrink-0 select-none pl-1 opacity-70"
              >
                {SIGN[row.type]}
              </span>
              {/* pre, not pre-wrap: wrapping a code line breaks the one-line
                  correspondence a diff is read by. The block scrolls instead. */}
              <span className="whitespace-pre pr-2">{row.text || ' '}</span>
            </div>
          )
        })}
      </div>
      {preview.truncated ? (
        <div className="border-border border-t bg-muted/40 px-2 py-1 text-[0.65rem] text-muted-foreground">
          Diff truncated. Open the file to see the rest.
        </div>
      ) : null}
    </div>
  )
}
